import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import loadable from '@loadable/component';

// Utils
import getWindow from 'Utils/get-window';
import {
  buildPrerollAdTagUrl,
  getAdsErrorDetail,
  getOnlineFlag,
  isAdRequestFailedError,
  probeAdServer,
  PROBE,
  withAutoplaySignal,
} from 'Utils/video-ad-request';
import GlobalVars from 'Utils/global-vars';
import sentry from 'Utils/sentry';
import loadIMA from 'Utils/load-ima';
// import { getAdForVideo } from 'Utils/api/ads';

import ErrorBoundary from 'ComponentLibrary/errors/error-boundary';

// hocs
import withThanksSubscription from '../../hocs/withThanksSubscription';

import connector from 'Utils/connector';
import connectFirstPartyVideo from 'Webapp/shared/app/connectors/connectFirstPartyVideo';
import connectSendUsageEventWithSession from 'Webapp/shared/app/connectors/connectSendUsageEventWithSession';
import withIsIos from '../../hocs/withIsIos';
import withT from 'ComponentLibrary/hocs/withT';
import VideoTracker from 'Webapp/shared/utils/video-tracker';

let Videojs = () => null;
let VideojsContribAds = () => null;
let VideojsIMA = () => null;
let CanAutoplay = () => null;

if (!GlobalVars.isServer()) {
  Videojs = loadable.lib(() => import('video.js'));
  VideojsContribAds = loadable.lib(() => import('videojs-contrib-ads'));
  VideojsIMA = loadable.lib(() => import('videojs-ima'));
  CanAutoplay = loadable.lib(() => import('can-autoplay'));
}

// BEGIN BIG HACK
//
// TODO remove this hack
//
// After switching to webpack 5 we are seeing the refs for
// loadable.lib only working one time.  The second time the components
// created by loadable.lib were mounted, the ref callback was not ever
// being called.

// The idea behind this hack is that it works the first time, so we
// store it and access it that way in the future rather than getting
// it from the component ref each time

// FWIW we also tried updating the @loadable/* libraries and that did
// not help, seems like the issue hasn't been addressed.

// See:
// our switch to webpack 5
// https://github.com/Flipboard/web-universal/pull/3356/

// possible related bug / issue with loadable
// https://github.com/gregberge/loadable-components/issues/829

// object to store dynamic libs in these libs will be accessed by
// loadableLibs[key] in component logic
//
// :(
const loadableLibs = {};
const memoizeLoadableLib = (key) => (ref) => {
  // if we have dynamic lib at key arleady do nothing
  if (loadableLibs[key]) {
    return;
  }
  // if the ref looks like an import, store at key
  if (ref?.default) {
    loadableLibs[key] = ref.default;
  }
};
// END BIG HACK

// The Google IMA SDK loads async from imasdk.googleapis.com, a domain that ad
// blockers block. setupVideo retries every ~100ms while waiting for it; this is
// how many retries we allow before concluding the SDK was blocked (rather than
// merely slow to load) and hard-gating playback. ~3s.
const AD_BLOCK_GRACE_ATTEMPTS = 30;

// On-site (non-embedded), AUTOPLAYING pre-roll: how long to wait for the ad
// before giving up and playing content. Click-to-play players are exempt
// (see deadlineApplies) — nothing is held back while the poster is up.
//
// Three timers are involved and all are overridden together so the deadline is
// one number:
//   - IMA `vastLoadTimeout` (default 5000ms, PER WRAPPER): the ad response.
//   - contrib-ads `timeout` (default 5000ms): play -> adsready.
//   - contrib-ads `prerollTimeout`: for pre-rolls this REPLACES `timeout` and
//     spans play -> ad start; videojs-ima 2.0.1 defaults it to 1000ms. In our
//     flow play() is only called after adsready, so it normally covers just
//     adsready -> start, but it must not be able to undercut the deadline.
// `preventLateAdStart` closes the remaining gap (multi-wrapper chains that
// resolve after the deadline never start an ad over content).
// Tuned from the t= telemetry of build 851 (09-12/13, video_web): ad_ready
// arrives within 5s for 94.5% of ready ads, 7s for 98%, 10s for 100%; the
// IMA 1005 failures sit at the SDK timeout regardless of its value (they moved
// from ~5s to ~11s when it was raised) and never recover — those clients simply
// never get an answer. So: 7s keeps 98% of the rescued readies and gives the
// stuck ~13% of attempts three seconds back. Embeds keep the defaults
// (publisher pages, no long black frame).
const ONSITE_PREROLL_TIMEOUT_MS = 7000;
// Adaptive cut: the ad-host probe (1.5s, no-cors) run at the start of the
// attempt predicts the never-completing requests — in production 1005s carried
// probe=timeout ~3:1 over probe=reachable. When the early probe times out,
// the client deadline for THIS attempt is pulled in to this value instead of
// making a viewer on a stalled connection sit through the full wait.
const ONSITE_PREROLL_SLOW_CLIENT_TIMEOUT_MS = 3000;

class HTMLVideo extends Component {
  constructor(props) {
    super(props);
    this.videoElementRef = React.createRef();

    this.loadVideoJSTimer = null;
    this.requestedSignedMediaCookie = false;
    this.midrollTriggered = false;

    this.refreshSignedMediaCookie = this.refreshSignedMediaCookie.bind(this);
    this.setupVideo = this.setupVideo.bind(this);
    this.shouldShowAd = this.shouldShowAd.bind(this);
    this.onError = this.onError.bind(this);
    this.getSource = this.getSource.bind(this);
    this.sourceRequiresCredentials = this.sourceRequiresCredentials.bind(this);
    this.handleUnmute = this.handleUnmute.bind(this);
    this.state = {
      adsResponse: null,
      setupComplete: false,
      adsBlocked: false,
      // True while the muted-autoplay fallback is playing muted and the
      // viewer has not unmuted yet (drives the "Tap to unmute" affordance).
      mutedAutoplay: false,
    };
  }

  async componentDidMount() {
    loadIMA();
    if (this.sourceRequiresCredentials()) {
      await this.refreshSignedMediaCookie();
    }
    this.setupVideo();
    // getAdForVideo().then(adsResponse => this.setState({ adsResponse }));
  }

  componentDidUpdate(prevProps) {
    if (!this.player) {
      return;
    }
    const { play } = this.props;
    if (!this.player.seeking() && prevProps.play !== play) {
      this.player[play ? 'play' : 'pause']();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this.player) {
      this.player.dispose();
    }
    if (this.fullscreenChangeHandler) {
      document.removeEventListener(
        'fullscreenchange',
        this.fullscreenChangeHandler,
      );
    }
    if (this.videoElementRef.current) {
      if (this.webkitBeginFullscreenHandler) {
        this.videoElementRef.current.removeEventListener(
          'webkitbeginfullscreen',
          this.webkitBeginFullscreenHandler,
        );
      }
      if (this.webkitEndFullscreenHandler) {
        this.videoElementRef.current.removeEventListener(
          'webkitendfullscreen',
          this.webkitEndFullscreenHandler,
        );
      }
    }
    getWindow().clearTimeout(this.loadVideoJSTimer);
    getWindow().clearTimeout(this.setupCompleteTimer);
    getWindow().clearTimeout(this.fullscreenResumeTimer);
    getWindow().clearTimeout(this.disposePlayerTimer);
    getWindow().clearTimeout(this.prerollDeadlineTimer);
  }

  async refreshSignedMediaCookie() {
    await this.props.refreshSignedMediaCookie();
    // If the above doesn't actually work, and the src in question
    // requires signed cookies, loading the media will fail.  If the
    // src does not require it, the video should still work
    this.requestedSignedMediaCookie = true;
  }

  // Returns { show, reason }. When not showing, `reason` sizes the invisible
  // upstream pre-roll loss — opportunities where no ad_request ever fires, so
  // they're absent from the ad funnel. 'not_eligible' = item.adPreRollOk is
  // false, which is where server-side gating (brand safety, domain, and the
  // missing-duration case Sami flagged) lands.
  evaluatePreRoll() {
    const {
      prerollAds,
      validThanksSubscription,
      shownVideoAd,
      adShowPercentage,
      videoAdsEnabled,
      forceVideoAds,
    } = this.props;
    if (!(videoAdsEnabled || forceVideoAds)) {
      return { show: false, reason: 'ads_disabled' };
    }
    if (!prerollAds) {
      return { show: false, reason: 'not_eligible' };
    }
    if (validThanksSubscription) {
      return { show: false, reason: 'subscriber' };
    }
    if (!shownVideoAd) {
      return { show: true, reason: null };
    }
    return Math.random() * 100 < adShowPercentage
      ? { show: true, reason: null }
      : { show: false, reason: 'frequency' };
  }

  shouldShowAd() {
    return this.evaluatePreRoll().show;
  }

  getSource() {
    const { source } = this.props;
    let src = source;

    // TODO here to debug / block VE problem, remove later
    if (src.indexOf('s3.amazonaws.com/ve1') > -1) {
      src = 'https://cdn.flipboard.com/dev_O/unavailable.mp4';
    }
    return src || '';
  }

  sourceRequiresCredentials() {
    return this.getSource().indexOf('ms.flipboard.com') > -1;
  }

  setupVideo() {
    const { isIos, isEmbedded, sendUsageEventWithSession } = this.props;
    // this is async loaded in footer-assets
    const googleIMAloaded = getWindow().google && getWindow().google.ima;
    // The autoplay probe (and its muted fallback) now runs on iOS too, so
    // wait for the library everywhere.
    const autoplayNotReady = !loadableLibs.canAutoplay;

    const credentialsNotReady = this.sourceRequiresCredentials()
      ? !this.requestedSignedMediaCookie
      : false;

    // Decide once whether this player will attempt an ad. evaluatePreRoll() uses a
    // random roll, so cache it to keep the decision stable across setup retries and
    // to reuse below. It legitimately returns show:false for ads_disabled /
    // not_eligible / subscriber / frequency — those plays should just play the
    // content and must never be gated.
    if (!this.preRollDecision) {
      this.preRollDecision = this.evaluatePreRoll();
    }
    // The Google IMA SDK (imasdk.googleapis.com) is a domain ad blockers block. We
    // only need it when we're actually going to show an ad — a pre-roll, or a
    // midroll embed. For no-ad plays, don't wait on it: set up and play normally.
    const needsIMA = this.preRollDecision.show || this.props.midrollTime > 0;
    const imaNeededButMissing = needsIMA && !googleIMAloaded;

    if (
      imaNeededButMissing ||
      !loadableLibs.videojs ||
      !loadableLibs.videojsContribAds ||
      !loadableLibs.videojsIMA ||
      credentialsNotReady ||
      autoplayNotReady
    ) {
      // If the only thing still missing is the (blocked) IMA SDK, while everything
      // we control (our bundle chunks, credentials, autoplay check) has loaded, an
      // ad blocker stripped the SDK. After a short grace period (so a merely
      // slow-loading SDK isn't walled), hard-gate this ad-bearing play rather than
      // serving the content ad-free, and record the detection in usage.
      const adBlockerDetected =
        imaNeededButMissing &&
        loadableLibs.videojs &&
        loadableLibs.videojsContribAds &&
        loadableLibs.videojsIMA &&
        !credentialsNotReady &&
        !autoplayNotReady;
      this.setupAttempts = (this.setupAttempts || 0) + 1;
      if (adBlockerDetected && this.setupAttempts >= AD_BLOCK_GRACE_ATTEMPTS) {
        this.handleAdBlocked('ima_sdk_blocked');
        return;
      }
      this.loadVideoJSTimer = getWindow().setTimeout(() => {
        this.setupVideo();
      }, 100);
      return;
    }

    if (!loadableLibs.videojs.getPlugin('ads')) {
      loadableLibs.videojs.registerPlugin(
        'ads',
        loadableLibs.videojsContribAds,
      );
    }

    // Register seek back/forward buttons as Video.js components
    const VjsButton = loadableLibs.videojs.getComponent('Button');
    if (!loadableLibs.videojs.getComponent('SeekBackBtn')) {
      const SeekBackBtn = loadableLibs.videojs.extend(VjsButton, {
        constructor: function constructor(player, options) {
          VjsButton.call(this, player, options);
          this.addClass('vjs-seek-back-btn');
          this.controlText('Go back 15 seconds');
        },
        handleClick: function handleClick() {
          const p = this.player();
          p.currentTime(Math.max(0, p.currentTime() - 15));
        },
      });
      loadableLibs.videojs.registerComponent('SeekBackBtn', SeekBackBtn);
    }
    if (!loadableLibs.videojs.getComponent('SeekFwdBtn')) {
      const SeekFwdBtn = loadableLibs.videojs.extend(VjsButton, {
        constructor: function constructor(player, options) {
          VjsButton.call(this, player, options);
          this.addClass('vjs-seek-fwd-btn');
          this.controlText('Skip ahead 30 seconds');
        },
        handleClick: function handleClick() {
          const p = this.player();
          const dur = p.duration() || Infinity;
          p.currentTime(Math.min(dur, p.currentTime() + 30));
        },
      });
      loadableLibs.videojs.registerComponent('SeekFwdBtn', SeekFwdBtn);
    }

    // Resolve whether autoplay will actually happen BEFORE building the ad tag,
    // so `vpa` and IMA's adWillAutoPlay describe the real playback mode: on a
    // browser that blocks autoplay we fall back to poster + click-to-play
    // (showVideoNonIos), which is `vpa=click` to buyers. Probe the mode we will
    // actually play in (muted embeds are allowed where unmuted autoplay is
    // not). iOS is probed like everything else: iOS Safari denies unmuted
    // autoplay without a gesture but allows muted inline autoplay, which is
    // exactly the case the muted fallback exists for.
    //
    // Muted-autoplay fallback (on-site only): build 853 telemetry showed
    // click-to-play (ap=0) is 84% of pre-roll attempts and converts at 1.0%,
    // against 34.6% for autoplay (ap=1) and 33.2% for the muted fallback
    // (ap=m) — i.e. wherever the fallback engages it fully recovers the ad
    // opportunity, but it only engaged on 5% of attempts.
    //
    // The reason is the probe, not browser policy: can-autoplay's muted test
    // element carries no `playsinline`, so mobile browsers that DO permit
    // muted autoplay report it as denied, and those attempts fell through to
    // click-to-play. So do not probe the muted case at all. Muted autoplay is
    // permitted near-universally, so assume it and let the real `play()`
    // promise be the authority: a rejection downgrades to poster +
    // click-to-play in playOrShowPoster() below and logs ad_autoplay_denied,
    // which keeps the optimistic assumption auditable.
    // Embeds keep their explicit `muted` behaviour.
    if (this.props.autoPlay && this.autoplayAllowed === undefined) {
      const requestedMuted = !!this.props.muted;
      loadableLibs.canAutoplay
        .video({ muted: requestedMuted })
        .then((result) => {
          if (result.result !== false) {
            this.autoplayAllowed = true;
            this.autoplayMuted = requestedMuted;
          } else if (!requestedMuted && !this.props.isEmbedded) {
            this.autoplayAllowed = true;
            this.autoplayMuted = true;
          } else {
            this.autoplayAllowed = false;
            this.autoplayMuted = false;
          }
          if (!this.unmounted) {
            this.setupVideo();
          }
        });
      return;
    }
    const willAutoPlay =
      !!this.props.autoPlay && this.autoplayAllowed !== false;
    // The mode we will actually play in: an explicit `muted` prop, or the
    // muted-autoplay fallback engaged above.
    const playMuted =
      !!this.props.muted || (willAutoPlay && !!this.autoplayMuted);
    const mutedFallbackActive = playMuted && !this.props.muted;
    // Telemetry flag carried on ad_attempt / ad_ready / ad_impression so the
    // funnel can be split by playback mode: 1 = autoplay, m = muted-autoplay
    // fallback, 0 = click-to-play (poster shown, waiting for the viewer).
    let autoplayMode = '1';
    if (!willAutoPlay) {
      autoplayMode = '0';
    } else if (mutedFallbackActive) {
      autoplayMode = 'm';
    }

    const {
      poster,
      videoJSOptions,
      track,
      videoTrackerOptions,
      videoEvents,
      videoAdOptions,
      overrideAdTagUrl,
      shownVideoAd,
      mimeType,
      t,
    } = this.props;

    // Build the pre-roll tag from this item's videoAdOptions. (The connector's
    // `videoAdTagUrl` selector reads redux state that has no reducer, so it
    // always produced a bare tag: no cust_params, description_url or plcmt —
    // which is why on-site web pre-rolls filled far below the embedded player.)
    // An override tag (embedded player) was built before the autoplay probe
    // ran, so normalize its `vpa` to the resolved decision: tag, IMA
    // adWillAutoPlay and playback must all agree.
    const effectiveAdTagUrl = overrideAdTagUrl
      ? withAutoplaySignal(overrideAdTagUrl, willAutoPlay)
      : buildPrerollAdTagUrl(videoAdOptions, {
          autoPlay: willAutoPlay,
          muted: playMuted,
        });
    // Reuse the decision cached above (do not re-roll the random frequency).
    const { show: showAd, reason: preRollSkipReason } = this.preRollDecision;

    const src = this.getSource();

    this.player = loadableLibs.videojs(
      this.videoElementRef.current,
      {
        // black poster until we have decided what we are doing
        poster:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQAAAAA3bvkkAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==',
        fill: true,
        controls: true,
        // Apply the playback mute to the player itself (explicit `muted` prop
        // or the muted-autoplay fallback), so it actually plays in the mode the
        // autoplay probe tested and that vpmute / adWillPlayMuted advertise.
        muted: playMuted,
        ...(isEmbedded && { inactivityTimeout: 4000 }),
        ...(isEmbedded && {
          controlBar: {
            children: [
              'playToggle',
              'currentTimeDisplay',
              'SeekBackBtn',
              'progressControl',
              'SeekFwdBtn',
              'remainingTimeDisplay',
              'fullscreenToggle',
            ],
          },
        }),
        sources: [
          {
            src,
            type: mimeType,
            withCredentials: this.sourceRequiresCredentials(),
          },
        ],
        ...videoJSOptions,
      },
      () => {
        const video = this.videoElementRef.current;
        if (video) {
          if (track) {
            new VideoTracker(
              video,
              videoTrackerOptions,
              sendUsageEventWithSession,
            );
          }
        }

        // Start playback and let the browser be the authority on whether the
        // assumed muted autoplay is actually permitted. Only a POLICY denial
        // counts, i.e. NotAllowedError (the name contrib-ads itself keys on):
        // every other rejection reaching here is contrib-ads managing the
        // pre-roll around us, and treating it as a denial would unmute and
        // poster a play that is about to happen. In videojs-contrib-ads 6.9.0:
        //   - desktop: `callPlay` returns middleware TERMINATOR while an ad is
        //     pending, and a terminated play() RESOLVES, so we see nothing.
        //   - iOS/Android: `isMiddlewareMediatorSupported()` is false, so play
        //     is blocked by other means and can reject with AbortError.
        // A denial therefore surfaces on the next play() that contrib-ads is
        // not blocking (after the ad starts, errors or times out), so this is a
        // floor on denials, not a total — the ap= split in usage remains the
        // authoritative measure.
        const playOrShowPoster = () => {
          const played = this.player.play();
          if (!played || typeof played.catch !== 'function') {
            return;
          }
          played.catch((error) => {
            if (this.unmounted || !this.player) {
              return;
            }
            if (!error || error.name !== 'NotAllowedError') {
              return;
            }
            this.player.poster(poster);
            if (mutedFallbackActive) {
              // We only muted to make autoplay possible and it was refused
              // anyway, so restore sound: the viewer's own click should play
              // with audio, and the unmute affordance is no longer meaningful.
              this.player.muted(false);
              this.setState({ mutedAutoplay: false });
              // Ad-funnel event only when a pre-roll was actually attempted;
              // the no-ad branch (ineligible / subscriber / frequency) also
              // reaches here and must not add an outcome with no ad_attempt.
              if (showAd) {
                logAdOutcome('ad_autoplay_denied', { url: 'ap=m' });
              }
            }
          });
        };

        const showVideoIos = () => {
          this.player.poster(poster);
          // HACK to remove spinner in iOS
          this.setupCompleteTimer = getWindow().setTimeout(() => {
            this.player.removeClass('vjs-waiting');
            this.setState({
              setupComplete: true,
            });
            // Honor the resolved autoplay decision (unmuted, muted fallback,
            // or click-to-play) instead of calling play() unconditionally —
            // a denied play() left iOS viewers on the poster, mislabeled as
            // autoplay.
            if (willAutoPlay) {
              playOrShowPoster();
            }
          }, 100);
        };
        const showVideoNonIos = () => {
          // Reuse the autoplay decision resolved before the ad tag was built
          // (willAutoPlay), so playback, `vpa` and IMA adWillAutoPlay can never
          // disagree — no second canAutoplay probe here.
          this.setupCompleteTimer = getWindow().setTimeout(
            () =>
              this.setState({
                setupComplete: true,
              }),
            100,
          );
          if (!willAutoPlay) {
            // if we can't autoplay show the poster
            this.player.poster(poster);
            return;
          }
          playOrShowPoster();
        };
        const showVideo = isIos ? showVideoIos : showVideoNonIos;

        // Web video-ad telemetry. iOS/Android ad outcomes are visible via GAM
        // Data Transfer, but web VAST outcomes are otherwise invisible — the
        // adserror handler below silently drops to content with no signal. Emit
        // a usage event at each IMA lifecycle stage so fill / impression / error
        // rates are measurable: ad_request = denominator, ad_impression =
        // rendered, ad_error / ad_nofill = the "no ad shown" cases.
        // Log the stable GAM ad unit (the `iu` param), not the full tag URL —
        // the URL carries cust_params/targeting that would bloat cardinality and
        // leak targeting context into analytics.
        let adUnit;
        try {
          adUnit =
            new URL(effectiveAdTagUrl).searchParams.get('iu') || undefined;
        } catch (e) {
          adUnit = undefined;
        }
        const logAdOutcome = (action, extra = {}) => {
          const { sendUsageEventWithSession, videoTrackerOptions = {} } =
            this.props;
          if (!sendUsageEventWithSession) {
            return;
          }
          // Dedup: IMA events ('ads-ad-started' etc.) can fire more than once
          // against a single player, over-counting impressions/errors vs
          // requests. This component owns one player for one video, so a set that
          // logs each action at most once keeps request/impression/error
          // reconcilable (no per-source reset needed).
          if (!this._adLoggedActions) {
            this._adLoggedActions = new Set();
          }
          if (this._adLoggedActions.has(action)) {
            return;
          }
          this._adLoggedActions.add(action);
          try {
            sendUsageEventWithSession({
              event_category: 'ad',
              event_action: action,
              // Keys must map to real usage_event columns (method / target_id /
              // item_type / url) to survive the pipeline — custom keys are dropped.
              event_data: {
                method: this.props.isEmbedded ? 'video_embed' : 'video_web',
                item_type: this.props.midrollTime > 0 ? 'midroll' : 'preroll',
                target_id: adUnit,
                item_id: videoTrackerOptions.item_id,
                section_id: videoTrackerOptions.section_id,
                item_partner_id: videoTrackerOptions.item_partner_id,
                ...extra,
              },
            });
          } catch (e) {
            sentry.captureException(e);
          }
        };

        const { midrollTime } = this.props;
        if (showAd) {
          // Funnel denominators (one each per setup; this player serves one
          // video, so the dedup set bounds every outcome to once):
          //   ad_attempt = every pre-roll we tried, logged before IMA runs.
          //   ad_request = attempts that demonstrably reached the ad server:
          //     logged alongside any outcome other than a client-blocked IMA
          //     1005, so ad_impression / ad_request is a demand-only fill rate.
          // If this player is ever reused across videos, reset the dedup set in
          // that explicit source-swap path.
          logAdOutcome('ad_attempt', { url: `ap=${autoplayMode}` });
          // Elapsed ms since the attempt, stamped as " t=<ms>" on ad_ready and
          // ad_error so the ready-latency distribution and the time-to-failure
          // cluster are measurable (Redshift `time` is not usable for this).
          // Instance field so the midroll path can re-stamp it right before its
          // own requestAds(): otherwise a midroll's `t` would include all the
          // content playback time since setup.
          this.adAttemptAt = Date.now();
          const elapsedMs = () => Date.now() - this.adAttemptAt;
          const logAdRequest = () => logAdOutcome('ad_request');
          // ---- On-site pre-roll client deadline -------------------------------
          // play() is deferred until adsready, so contrib-ads' timers cannot
          // start before IMA answers, and IMA's vastLoadTimeout is per wrapper —
          // a multi-wrapper chain could otherwise keep the poster up well past
          // the wait. An independent deadline from the attempt closes that. Every
          // path judges lateness by ELAPSED TIME against the per-attempt
          // deadline (this.prerollDeadlineMs: 7s, or 3s after an early-probe
          // timeout), not only the timer flag, so a setTimeout callback delayed
          // by the event loop cannot let a late adsready / adserror / probe
          // result take the normal path.
          // The deadline for THIS attempt (ms since ad_attempt). Starts at the
          // full wait; the early ad-host probe may pull it in (see below).
          this.prerollDeadlineMs = ONSITE_PREROLL_TIMEOUT_MS;
          // Set once the attempt has resolved normally (ad ready and started
          // toward playback, or a non-blocked error fell back to content).
          // Nothing may arm or fire the deadline after this.
          this.prerollSettled = false;
          const settlePreroll = () => {
            this.prerollSettled = true;
            getWindow().clearTimeout(this.prerollDeadlineTimer);
          };
          // The deadline exists to stop CONTENT being held back waiting for an
          // ad. A click-to-play player holds nothing back — the poster is up
          // until the viewer clicks — so it must never arm: build 853 showed it
          // firing on 50% of ap=0 attempts, where triggering adtimeout +
          // skipLinearAdMode discarded an ad that would otherwise have played
          // on the click (and produced ad_ready_late instead).
          const deadlineApplies =
            !isEmbedded && midrollTime <= 0 && willAutoPlay;
          const pastPrerollDeadline = () =>
            deadlineApplies && elapsedMs() >= this.prerollDeadlineMs;
          const onPrerollDeadline = () => {
            if (
              this.unmounted ||
              this.prerollDeadlinePassed ||
              this.prerollSettled
            ) {
              return;
            }
            this.prerollDeadlinePassed = true;
            getWindow().clearTimeout(this.prerollDeadlineTimer);
            // deadline=client plus the early probe verdict (if it resolved) and
            // the deadline that applied, so the adaptive cut is measurable.
            logAdOutcome('ad_timeout', {
              url: `t=${elapsedMs()} deadline=client limit=${
                this.prerollDeadlineMs
              }${
                this.earlyProbeStatus ? ` probe=${this.earlyProbeStatus}` : ''
              }`,
            });
            if (this.player) {
              // (1) contrib-ads' own adtimeout event: videojs-ima marks the
              // attempt timed out and, with preventLateAdStart, DROPS any later
              // adsready outright — autoplay and click-to-play alike, since it
              // does not depend on play() having been called. (2) tell
              // contrib-ads there is no pre-roll for this play, so the play()
              // we issue now, or the user's later click, resumes content
              // directly instead of waiting on another timer.
              this.player.trigger('adtimeout');
              if (
                this.player.ads &&
                typeof this.player.ads.skipLinearAdMode === 'function'
              ) {
                this.player.ads.skipLinearAdMode();
              }
            }
            showVideo();
          };
          // Returns true when the deadline has passed (running the deadline
          // path first if the timer callback has not fired yet).
          const ensurePrerollDeadline = () => {
            if (!this.prerollDeadlinePassed && pastPrerollDeadline()) {
              onPrerollDeadline();
            }
            return this.prerollDeadlinePassed;
          };

          this.player.on('adsready', () => {
            // Readiness at/after the client deadline is not a normal ready
            // outcome: the deadline path owns it (ad_timeout) and the start is
            // dropped. Record it separately so late arrivals stay measurable
            // without inflating ad_request / ad_ready.
            if (ensurePrerollDeadline()) {
              logAdOutcome('ad_ready_late', { url: `t=${elapsedMs()}` });
              return;
            }
            logAdRequest();
            logAdOutcome('ad_ready', {
              url: `t=${elapsedMs()} ap=${autoplayMode}`,
            });
          });
          this.player.on('ads-ad-started', () => {
            logAdRequest();
            // t= on the impression too: ad_ready -> ad_impression converts only
            // ~39% of the time and this is the first step to seeing whether the
            // gap is ad-media load time or viewers leaving.
            logAdOutcome('ad_impression', {
              url: `t=${elapsedMs()} ap=${autoplayMode}`,
            });
          });
          this.player.on('ads-ad-ended', () => logAdOutcome('ad_complete'));
          this.player.on('nopreroll', () => {
            // contrib-ads resumes content on nopreroll: the attempt is over,
            // so nothing (e.g. a late early-probe timeout) may arm a deadline.
            settlePreroll();
            logAdRequest();
            logAdOutcome('ad_nofill');
          });
          this.player.on('adtimeout', () => {
            // A real contrib-ads timeout means the SDK was still working the
            // request (it reached the server); our synthetic client-deadline
            // adtimeout does not, so it must not inflate the demand-confirmed
            // ad_request denominator. (Its ad_timeout was already logged with
            // t= by the deadline path; this one dedups.)
            if (!this.prerollDeadlinePassed) {
              logAdRequest();
            }
            logAdOutcome('ad_timeout');
          });
          // event_url carries "vast=<code> code=<code> <message>" so the
          // "No Ads VAST response after Wrappers" volume splits by cause:
          // VAST 303 = no demand, 301/302 = wrapper problem, 100 = parse,
          // IMA 1005 = the request itself failed.
          // `context` rides along in event_url as fixed-shape " k=v" tokens
          // in a documented order — caller context (probe=…) first, then
          // online=… — followed by the IMA inner error, so the 1005 bucket can
          // be split by cause.
          // `t` is the elapsed ms when adserror ARRIVED — callers that defer
          // logging (the 1005 probe path) pass the value they captured at
          // arrival so the probe's own latency never lands in the number.
          const logAdsError = (e, context = {}, t = elapsedMs()) => {
            // After the client deadline the attempt is already accounted for
            // as ad_timeout; a late error must not add a demand-confirmed
            // ad_request on top.
            if (!this.prerollDeadlinePassed) {
              logAdRequest();
            }
            logAdOutcome('ad_error', {
              url: getAdsErrorDetail(e, {
                ...context,
                online: getOnlineFlag(),
                t,
              }).url,
            });
          };
          // Pre-roll only: IMA 1005 is generic FAILED_TO_REQUEST_ADS, so it is
          // confirmed with an independent probe of the GAM ad host before we
          // call it a blocker. Only a confirmed 'unreachable' hard-gates +
          // logs ad_blocked (kept out of ad_error / ad_request so the fill
          // funnel stays demand-only). Every other status (reachable, timeout,
          // unavailable) is logged verbatim as probe=<status> on ad_error and
          // the content plays ad-free.
          const handlePrerollAdsError = (e, onNotBlocked) => {
            const tAtError = elapsedMs();
            // Terminal cases — record the outcome only, never gate or re-play:
            //  * the attempt already settled (ad ready and handed to playback,
            //    or an earlier error already fell back to content; contrib-ads
            //    owns recovery from an ad that fails after it was ready), or
            //  * the client deadline passed (by flag or by elapsed time).
            // A late 1005 still runs the probe so the telemetry line keeps its
            // probe=<status> split.
            if (this.prerollSettled || ensurePrerollDeadline()) {
              if (isAdRequestFailedError(e)) {
                probeAdServer().then((status) => {
                  if (!this.unmounted) {
                    logAdsError(e, { probe: status }, tAtError);
                  }
                });
              } else {
                logAdsError(e, {}, tAtError);
              }
              return;
            }
            if (!isAdRequestFailedError(e)) {
              settlePreroll();
              logAdsError(e, {}, tAtError);
              onNotBlocked();
              return;
            }
            // 1005: the deadline stays ARMED through the probe, so a late error
            // plus a slow probe can never hold content past the deadline. The
            // probe callback then honors an already-passed deadline.
            probeAdServer().then((status) => {
              if (this.unmounted) {
                return;
              }
              // Same terminal judgement here: a probe resolving after the
              // attempt settled (adsready / nopreroll landed while it was in
              // flight) or at/past the per-attempt deadline must never gate or
              // re-play — log only.
              if (this.prerollSettled || ensurePrerollDeadline()) {
                logAdsError(e, { probe: status }, tAtError);
                return;
              }
              settlePreroll();
              if (status === PROBE.UNREACHABLE) {
                this.handleAdBlocked('ad_request_blocked');
              } else {
                logAdsError(e, { probe: status }, tAtError);
                onNotBlocked();
              }
            });
          };
          if (midrollTime > 0) {
            this.player.on('adserror', (e) => logAdsError(e));
          }

          if (midrollTime > 0) {
            // Midroll: start content immediately, ad triggers later
            showVideo();
            // Mark shownVideoAd when the midroll ad actually starts
            this.player.on('ads-ad-started', () => {
              if (!shownVideoAd) {
                this.props.setShownVideoAd(true);
              }
            });
          } else {
            // Preroll: wait until the ad is ready to play. On-site players arm
            // the independent client deadline (see above).
            //
            // A click-to-play player is revealed RIGHT AWAY: nothing is being
            // held back for it, so there is no reason to keep the poster and
            // its play button hidden behind a pending ad request (and with the
            // deadline exempt there would be no backstop to reveal it at all).
            // The ad request stays in flight; if it is ready when the viewer
            // clicks, contrib-ads plays it, otherwise its own timeout starts
            // the content.
            // showVideo() on a non-autoplaying player only applies the real
            // poster and setupComplete — it never calls play() — and the later
            // adsready/adserror call is idempotent, so revealing here is safe.
            if (!willAutoPlay) {
              showVideo();
            }
            if (deadlineApplies) {
              this.prerollDeadlineTimer = getWindow().setTimeout(
                onPrerollDeadline,
                ONSITE_PREROLL_TIMEOUT_MS,
              );
              // Early ad-host probe, in parallel with IMA's own request. A
              // probe that cannot even get an opaque answer in 1.5s predicts an
              // ad request that will never complete, so pull the deadline in
              // for this attempt rather than hold content the full wait.
              // reachable / unreachable change nothing here: unreachable is
              // only acted on when IMA itself fails (the 1005 gate path).
              probeAdServer().then((status) => {
                // A probe resolving after the attempt already settled (ad
                // ready / fallback to content) must never re-arm a deadline
                // against running content or a started ad. That includes a
                // TIMEOUT landing after an adsready that arrived inside the
                // probe's own 1.5s: an ad answer that fast means the client is
                // not stalled, so the timeout is stale and the remaining-budget
                // hand-off made at adsready stands.
                if (
                  this.unmounted ||
                  this.prerollDeadlinePassed ||
                  this.prerollSettled
                ) {
                  return;
                }
                this.earlyProbeStatus = status;
                if (status === PROBE.TIMEOUT) {
                  this.prerollDeadlineMs =
                    ONSITE_PREROLL_SLOW_CLIENT_TIMEOUT_MS;
                  getWindow().clearTimeout(this.prerollDeadlineTimer);
                  this.prerollDeadlineTimer = getWindow().setTimeout(
                    onPrerollDeadline,
                    Math.max(this.prerollDeadlineMs - elapsedMs(), 0),
                  );
                }
              });
            }
            this.player.on('adsready', () => {
              if (ensurePrerollDeadline()) {
                // Late readiness: videojs-ima drops it after our adtimeout
                // (preventLateAdStart) and contrib-ads resumes content via
                // skipLinearAdMode. Never start anything here.
                return;
              }
              settlePreroll();
              // Hand the REMAINING budget to contrib-ads for adsready -> ad
              // start (its prerollTimeout is read at play() time), instead of
              // granting a fresh window on top of the time already spent. No
              // floor: the client deadline is the deadline, so a near-deadline
              // adsready gets only what is left of it.
              if (
                deadlineApplies &&
                this.player.ads &&
                this.player.ads.settings
              ) {
                this.player.ads.settings.prerollTimeout = Math.max(
                  this.prerollDeadlineMs - elapsedMs(),
                  0,
                );
              }
              showVideo();
              // set shownVideoAd if we think we actually have
              if (!shownVideoAd) {
                this.props.setShownVideoAd(true);
              }
            });
            // No ad: a confirmed client-blocked request hard-gates like the
            // missing-SDK case; anything else plays the content ad-free.
            this.player.on('adserror', (e) =>
              handlePrerollAdsError(e, showVideo),
            );
          }
        } else {
          // Eligible video but pre-roll not requested — log why, so the
          // upstream loss (esp. 'not_eligible' = server-side gating incl.
          // missing duration) is measurable alongside the request-level funnel.
          logAdOutcome('preroll_skipped', { url: preRollSkipReason });
          showVideo();
        }
      },
    );

    if (mutedFallbackActive) {
      // Show "Tap to unmute" until the viewer unmutes (via our button or the
      // control bar). videojs-ima mirrors player volume onto the ad, so
      // unmuting the player unmutes a running ad too.
      this.setState({ mutedAutoplay: true });
      this.player.on('volumechange', () => {
        if (this.player && !this.player.muted() && this.state.mutedAutoplay) {
          this.setState({ mutedAutoplay: false });
        }
      });
    }

    if (isEmbedded) {
      // Tap on video area toggles play/pause and shows the control bar.
      // Video.js doesn't do this reliably on touch devices.
      this.player.on('touchend', (e) => {
        const target = e.target || e.srcElement;
        if (target && target.closest && target.closest('.vjs-control-bar')) {
          return;
        }
        if (this.player.paused()) {
          this.player.play();
        } else {
          this.player.pause();
        }
        this.player.userActive(true);
      });

      // Show control bar when paused
      this.player.on('pause', () => {
        this.player.userActive(true);
      });
    }

    // Fullscreen handling for embedded players.
    const videoEl = this.videoElementRef.current;
    if (isEmbedded && videoEl) {
      const fsButton = this.player.controlBar.getChild('fullscreenToggle');
      if (fsButton) {
        if (videoEl.webkitEnterFullscreen && !document.fullscreenEnabled) {
          // iOS WebView: the Fullscreen API (requestFullscreen) doesn't work.
          // Use webkitEnterFullscreen on the native video element instead.
          fsButton.handleClick = () => {
            videoEl.webkitEnterFullscreen();
          };
          // Resume playback after exiting iOS native fullscreen.
          // iOS fires pause BEFORE webkitendfullscreen, so we track
          // the playing state at fullscreen entry to know whether to
          // resume on exit.
          this.wasPlayingBeforeFullscreen = false;
          this.webkitBeginFullscreenHandler = () => {
            this.wasPlayingBeforeFullscreen = !this.player.paused();
            if (this.props.onFullscreenChange) {
              this.props.onFullscreenChange(true);
            }
          };
          videoEl.addEventListener(
            'webkitbeginfullscreen',
            this.webkitBeginFullscreenHandler,
          );
          this.webkitEndFullscreenHandler = () => {
            if (this.props.onFullscreenChange) {
              this.props.onFullscreenChange(false);
            }
            if (this.wasPlayingBeforeFullscreen) {
              this.fullscreenResumeTimer = getWindow().setTimeout(() => {
                if (this.player && this.player.paused()) {
                  this.player.play();
                }
              }, 200);
            }
          };
          videoEl.addEventListener(
            'webkitendfullscreen',
            this.webkitEndFullscreenHandler,
          );
        } else if (document.fullscreenEnabled) {
          // Web iframe: requestFullscreen on the player container only fills
          // the iframe, not the screen.  Request fullscreen on the document
          // element so the browser promotes the iframe to true fullscreen.
          fsButton.handleClick = () => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              document.documentElement.requestFullscreen();
            }
          };
          // Sync Video.js fullscreen class with actual document fullscreen state
          this.fullscreenChangeHandler = () => {
            if (document.fullscreenElement) {
              this.player.addClass('vjs-fullscreen');
              if (this.props.onFullscreenChange) {
                this.props.onFullscreenChange(true);
              }
            } else {
              if (this.props.onFullscreenChange) {
                this.props.onFullscreenChange(false);
              }
              const wasPlaying = !this.player.paused();
              this.player.removeClass('vjs-fullscreen');
              // Browsers may transiently pause the video during fullscreen
              // exit, which triggers onPause → setState({play:false}) and
              // locks the player in a paused state.  Resume after the
              // transition settles.
              if (wasPlaying) {
                this.fullscreenResumeTimer = getWindow().setTimeout(() => {
                  if (this.player && this.player.paused()) {
                    this.player.play();
                  }
                }, 200);
              }
            }
          };
          document.addEventListener(
            'fullscreenchange',
            this.fullscreenChangeHandler,
          );
        }
      }
    } else if (
      videoEl &&
      videoEl.webkitEnterFullscreen &&
      !document.fullscreenEnabled
    ) {
      // Non-embedded iOS fallback (legacy path)
      const fsButton = this.player.controlBar.getChild('fullscreenToggle');
      if (fsButton) {
        fsButton.handleClick = () => {
          videoEl.webkitEnterFullscreen();
        };
      }
    }

    this.player.on('ended', () => {
      this.player.poster(poster);
      this.player.hasStarted(false);
    });

    this.player.on('error', () => {
      if (GlobalVars.isDevelopment) {
        getWindow().alert(
          'Unable to play video.  This is development, is this a signed media thing? Do you have signed media cookies?  Visit this same page in production to get those.',
        );
      }
    });

    Object.keys(videoEvents).forEach((key) =>
      this.player.on(key, videoEvents[key]),
    );

    if (showAd) {
      const { midrollTime } = this.props;
      if (midrollTime > 0) {
        // Midroll: initialize IMA without adTagUrl so no preroll fires
        this.player.ima({
          adLabel: t('advertisement'),
          adWillAutoPlay: true,
          adWillPlayMuted: playMuted,
        });
        // Trigger midroll ad when playback reaches the specified time
        this.player.on('timeupdate', () => {
          if (
            !this.midrollTriggered &&
            this.player.currentTime() >= midrollTime
          ) {
            this.midrollTriggered = true;
            this.midrollResumeTime = this.player.currentTime();
            // The midroll ad attempt starts here, not at setup — re-stamp so
            // t= on adsready / adserror measures this request only.
            this.adAttemptAt = Date.now();
            this.player.ima.changeAdTag(effectiveAdTagUrl);
            this.player.ima.requestAds();
          }
        });
        // Safety net: restore playback position if the SDK resets it
        this.player.on('contentresumed', () => {
          if (
            this.midrollResumeTime &&
            this.player.currentTime() < this.midrollResumeTime - 1
          ) {
            this.player.currentTime(this.midrollResumeTime);
          }
          this.midrollResumeTime = null;
        });
      } else {
        // Preroll: standard IMA initialization with adTagUrl. On-site players
        // get a longer ad wait (see ONSITE_PREROLL_TIMEOUT_MS): the IMA VAST
        // load timeout and both contrib-ads timers move together, otherwise
        // contrib-ads gives up first and IMA's later adsready would arrive
        // over playing content.
        this.player.ima({
          adLabel: t('advertisement'),
          adTagUrl: effectiveAdTagUrl,
          adWillAutoPlay: willAutoPlay,
          adWillPlayMuted: playMuted,
          ...(!isEmbedded && {
            vastLoadTimeout: ONSITE_PREROLL_TIMEOUT_MS,
            contribAdsSettings: {
              timeout: ONSITE_PREROLL_TIMEOUT_MS,
              prerollTimeout: ONSITE_PREROLL_TIMEOUT_MS,
            },
            // vastLoadTimeout is PER WRAPPER, so a multi-wrapper chain can
            // still resolve after the overall contrib-ads deadline. Never let
            // that late adsready start an ad over content that already began.
            preventLateAdStart: true,
          }),
        });
      }
    }
  }

  onError(error, errorInfo) {
    if (error.message.match(/Loading chunk.*failed/)) {
      // An ad blocker stripped one of our video/ad bundle chunks.
      this.handleAdBlocked('chunk_load_failed');
    } else {
      sentry.captureError(error, errorInfo);
    }
  }

  // Hard-gate the video because an ad blocker was detected: show the
  // disable-ad-blocker message instead of playing, and record why in usage.
  handleAdBlocked(detection) {
    if (this.state.adsBlocked) {
      return;
    }
    getWindow().clearTimeout(this.loadVideoJSTimer);
    // setupComplete must flip too: `.video-player--waiting` keeps the wrapper
    // at opacity 0 until setup finishes, so a gate raised before showVideo()
    // ran (blocked SDK, blocked ad request) would otherwise render invisible.
    this.setState({ adsBlocked: true, setupComplete: true });
    this.logAdBlocked(detection);
    // A blocked ad request is detected after the player exists (from inside
    // its own adserror handler). Tear it down off the current tick so the gate
    // message can replace the <video> cleanly.
    if (this.player) {
      this.disposePlayerTimer = getWindow().setTimeout(() => {
        if (this.player) {
          try {
            this.player.dispose();
          } catch (e) {
            // already disposed
          }
          this.player = null;
        }
      }, 0);
    }
  }

  // Usage event marking a no-play caused by a detected ad blocker. `detection`
  // is how we caught it: 'ima_sdk_blocked' (google.ima never loaded),
  // 'chunk_load_failed' (our bundle chunk was stripped) or
  // 'ad_request_blocked' (SDK loaded but the gampad/ads request failed, IMA 1005).
  logAdBlocked(detection) {
    const {
      sendUsageEventWithSession,
      isEmbedded,
      videoTrackerOptions = {},
    } = this.props;
    if (!sendUsageEventWithSession) {
      return;
    }
    sendUsageEventWithSession({
      event_category: 'ad',
      event_action: 'ad_blocked',
      // Keys must map to real usage_event columns (method / item_type / url /
      // item_id / section_id) to survive the pipeline; custom keys are dropped.
      event_data: {
        method: isEmbedded ? 'video_embed' : 'video_web',
        // Same position derivation as the ad-outcome events, so a blocked midroll
        // embed isn't misclassified as a preroll.
        item_type: this.props.midrollTime > 0 ? 'midroll' : 'preroll',
        item_id: videoTrackerOptions.item_id,
        section_id: videoTrackerOptions.section_id,
        // event_url records how the block was detected.
        url: detection,
      },
    });
  }

  handleUnmute() {
    if (this.player) {
      this.player.muted(false);
    }
    this.setState({ mutedAutoplay: false });
  }

  render() {
    const { isIos, t } = this.props;
    const className = classNames('video-player', this.props.className, {
      'video-player--waiting': !this.state.setupComplete,
    });
    return (
      <div className={className}>
        {this.state.adsBlocked ? (
          <div className="video-player__disable-ad-blocker">
            <span>{t('disable_ad_blocker_to_see_content')}</span>
          </div>
        ) : (
          <div className="video-player--hide" data-vjs-player>
            <React.Fragment>
              <ErrorBoundary onError={this.onError}>
                <Videojs ref={memoizeLoadableLib('videojs')} />
                <VideojsContribAds
                  ref={memoizeLoadableLib('videojsContribAds')}
                />
                <VideojsIMA ref={memoizeLoadableLib('videojsIMA')} />
                <CanAutoplay ref={memoizeLoadableLib('canAutoplay')} />
              </ErrorBoundary>
              <video
                controls
                playsInline={isIos}
                preload="auto"
                className="video-js vjs-waiting vjs-big-play-centered"
                crossOrigin={
                  this.sourceRequiresCredentials()
                    ? 'use-credentials'
                    : undefined
                }
                ref={this.videoElementRef}
              />
              {this.state.mutedAutoplay && (
                <button
                  type="button"
                  className="video-player__unmute"
                  onClick={this.handleUnmute}
                >
                  {t('tap_to_unmute')}
                </button>
              )}
            </React.Fragment>
          </div>
        )}
      </div>
    );
  }
}

HTMLVideo.defaultProps = {
  videoEvents: {},
  videoAdOptions: {},
  muted: false,
  videoJSOptions: {},
  videoTrackerOptions: {},
  track: false,
  play: false,
  autoPlay: false,
  prerollAds: false,
  forceVideoAds: false,
  isEmbedded: false,
  validThanksSubscription: false,
  midrollTime: 0,
};

HTMLVideo.propTypes = {
  source: PropTypes.string.isRequired,
  videoEvents: PropTypes.object,
  videoJSOptions: PropTypes.object,
  videoTrackerOptions: PropTypes.object,
  track: PropTypes.bool,
  poster: PropTypes.string,
  className: PropTypes.string,
  prerollAds: PropTypes.bool,
  autoPlay: PropTypes.bool,
  play: PropTypes.bool,
  validThanksSubscription: PropTypes.bool,
  adShowPercentage: PropTypes.number.isRequired,
  videoAdOptions: PropTypes.object,
  overrideAdTagUrl: PropTypes.string,
  muted: PropTypes.bool,
  shownVideoAd: PropTypes.bool.isRequired,
  setShownVideoAd: PropTypes.func.isRequired,
  refreshSignedMediaCookie: PropTypes.func.isRequired,
  isIos: PropTypes.bool.isRequired,
  sendUsageEventWithSession: PropTypes.func.isRequired,
  videoAdsEnabled: PropTypes.bool.isRequired,
  forceVideoAds: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  onFullscreenChange: PropTypes.func,
  midrollTime: PropTypes.number,
  mimeType: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connector(
  connectFirstPartyVideo,
  connectSendUsageEventWithSession,
)(withT(withIsIos(withThanksSubscription(HTMLVideo))));
